import type { IssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { CHILD_ISSUES_PANEL_TYPE, SORT_OPTION } from '../../constants.tsx';
import type { ChildIssuesPanelType, SortOption } from '../../types.tsx';

export const generateSortBy = (sortByValue?: SortOption) => {
	if (!sortByValue) {
		return encodeURIComponent(' ORDER BY rank');
	}
	switch (sortByValue) {
		case SORT_OPTION.STATUS:
			return encodeURIComponent(' ORDER BY statusCategory, status asc, rank');
		case SORT_OPTION.PRIORITY:
		case SORT_OPTION.KEY:
		case SORT_OPTION.ASSIGNEE:
		case SORT_OPTION.CREATED:
			return encodeURIComponent(` ORDER BY ${sortByValue}, rank`);
		case SORT_OPTION.RANKING:
		default:
			return encodeURIComponent(' ORDER BY rank');
	}
};

// different between parent and parent link jql is that parent refers to subtasks/next gen child issues
// parent link refers to portfolio children issues
const generateParentJQL = (parentKey: IssueKey) => `${encodeURIComponent(`parent=${parentKey}`)}`;
const generateParentLinkJQL = (parentKey: IssueKey) =>
	`${encodeURIComponent(`"Parent Link"=${parentKey}`)}`;
const generateEpicLinkJQL = (parentKey: IssueKey) =>
	`${encodeURIComponent(`"Epic Link"=${parentKey}`)}`;

const generateOverLimitJQL = (parentKey: IssueKey, sortByValue?: SortOption) =>
	`jql=${generateParentJQL(parentKey)}${generateSortBy(sortByValue)}`;

// Non-system epics do not support Epic Link: https://hello.atlassian.net/wiki/spaces/~888416081/pages/2366083841/System+and+Non-system+epic+issue+types
// To search for their children, we have to additionally use Parent Link
// Once https://team.atlassian.com/project/ATLAS-15331 is complete, both should be replaced with the Parent JQL
// The work to replace it is tracked at https://hello.atlassian.net/browse/JPO-23454
const generateJQLForCMPParentAtHierarchyLevelOne = (
	parentKey: IssueKey,
	sortByValue?: SortOption,
) =>
	`jql=${generateEpicLinkJQL(parentKey)} OR ${generateParentLinkJQL(parentKey)}${generateSortBy(
		sortByValue,
	)}`;

export const generateChildIssuesJQL = (
	parentKey: IssueKey,
	childIssuesPanelType: ChildIssuesPanelType,
	hasExceededIssuesLimitInitialLoad: boolean,
	sortByValue?: SortOption,
) => {
	if (
		hasExceededIssuesLimitInitialLoad &&
		childIssuesPanelType !== CHILD_ISSUES_PANEL_TYPE.CHILDREN_ISSUES_PANEL
	) {
		return generateOverLimitJQL(parentKey, sortByValue);
	}

	switch (childIssuesPanelType) {
		case CHILD_ISSUES_PANEL_TYPE.CLASSIC_PORTFOLIO_CHILDREN:
			return `jql=${generateParentLinkJQL(parentKey)}${generateSortBy(sortByValue)}`;
		case CHILD_ISSUES_PANEL_TYPE.CLASSIC_PROJECT_EPIC_CHILDREN:
			return generateJQLForCMPParentAtHierarchyLevelOne(parentKey, sortByValue);
		case CHILD_ISSUES_PANEL_TYPE.CLASSIC_SUBTASKS:
		case CHILD_ISSUES_PANEL_TYPE.CHILDREN_ISSUES_PANEL:
		default:
			return `jql=${generateParentJQL(parentKey)}${generateSortBy(sortByValue)}`;
	}
};
